/** biome-ignore-all lint/correctness/useExhaustiveDependencies: apiUrlCreator */
import ArticleUpdaterService from "@components/Article/ArticleUpdater/ArticleUpdaterService";
import Icon from "@components/Atoms/Icon";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import Workspace from "@core-ui/ContextServices/Workspace";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import type MergeModal from "@ext/git/actions/Branch/components/MergeModal";
import type CreateMergeRequestModal from "@ext/git/actions/Branch/components/MergeRequest/CreateMergeRequest";
import tryOpenMergeConflict from "@ext/git/actions/MergeConflictHandler/logic/tryOpenMergeConflict";
import type MergeData from "@ext/git/actions/MergeConflictHandler/model/MergeData";
import { useMergeRequestStore } from "@ext/git/core/GitMergeRequest/logic/store/MergeRequestStore";
import type { CreateMergeRequest } from "@ext/git/core/GitMergeRequest/model/MergeRequest";
import DeleteItem from "@ext/item/actions/DeleteItem";
import t from "@ext/localization/locale/translate";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@ui-kit/Dropdown";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { DisabledReason } from "@ui-kit/Tooltip";
import { type ComponentProps, type ReactNode, useCallback, useState } from "react";

interface BranchMenuProps {
	currentBranchName: string;
	branchName: string;
	trigger: ReactNode;
	refreshList?: () => void;
	onMergeRequestCreate?: () => void;
}

const BranchMenu = (props: BranchMenuProps) => {
	const { currentBranchName, branchName, trigger, refreshList, onMergeRequestCreate } = props;

	const [isLoading, setIsLoading] = useState(false);

	const apiUrlCreator = ApiUrlCreatorService.value;
	const workspace = Workspace.current();
	const gesUrl = workspace?.enterprise?.gesUrl;
	const isEnterprise = !!gesUrl;
	const hasMergeRequest = useMergeRequestStore((state) => !!state.mergeRequest && !state.isDraft);

	const setCreateMergeRequestModal = useCallback(() => {
		ModalToOpenService.setValue<ComponentProps<typeof CreateMergeRequestModal>>(ModalToOpen.CreateMergeRequest, {
			isEnabledGetUsers: false,
			useGesUsersSelect: isEnterprise,
			sourceBranchRef: currentBranchName,
			targetBranchRef: branchName,
			onOpen: async () => {
				if (!isEnterprise) return;
				const result = await new EnterpriseApi(gesUrl).isEnabledGetUsers();
				ModalToOpenService.updateArgs<ComponentProps<typeof CreateMergeRequestModal>>((prevArgs) => ({
					...prevArgs,
					isEnabledGetUsers: result,
					useGesUsersSelect: isEnterprise,
				}));
			},
			onSubmit: async (mergeRequest: CreateMergeRequest) => {
				ModalToOpenService.updateArgs<ComponentProps<typeof CreateMergeRequestModal>>((prevArgs) => ({
					...prevArgs,
					isLoading: true,
				}));
				const res = await FetchService.fetch(apiUrlCreator.createMergeRequest(), JSON.stringify(mergeRequest));
				ModalToOpenService.resetValue();
				if (res.ok) onMergeRequestCreate?.();
			},
			onClose: () => {
				ModalToOpenService.resetValue();
			},
		});
	}, [currentBranchName, branchName, isEnterprise, gesUrl, onMergeRequestCreate, apiUrlCreator]);

	const instantMerge = useCallback(() => {
		ModalToOpenService.setValue<ComponentProps<typeof MergeModal>>(ModalToOpen.Merge, {
			sourceBranchRef: currentBranchName,
			targetBranchRef: branchName,
			onSubmit: async (mergeRequestOptions) => {
				ModalToOpenService.updateArgs((prevArgs) => ({ ...prevArgs, isLoading: true }));
				const res = await FetchService.fetch<MergeData>(
					apiUrlCreator.mergeInto(
						branchName,
						mergeRequestOptions?.deleteAfterMerge,
						mergeRequestOptions?.squash,
					),
				);
				ModalToOpenService.resetValue();
				await BranchUpdaterService.updateBranch(apiUrlCreator);
				await ArticleUpdaterService.update(apiUrlCreator);
				if (!res.ok) return;
				tryOpenMergeConflict({ mergeData: await res.json() });
				refreshList?.();
			},
			onClose: () => {
				ModalToOpenService.resetValue();
			},
		});
	}, [currentBranchName, branchName, refreshList]);

	const deleteBranch = useCallback(async () => {
		if (isLoading) return;

		try {
			setIsLoading(true);
			await FetchService.fetch(apiUrlCreator.getVersionControlDeleteBranchUrl(branchName));
			// After the answer, not before it: the list is fetched anew, and a refresh started
			// ahead of the delete reads the branch that is about to go and leaves it on screen —
			// nothing refreshes the panel again. `instantMerge` above refreshes in this order.
			refreshList?.();
		} finally {
			setIsLoading(false);
		}
	}, [branchName, refreshList, isLoading]);

	return (
		<ComponentVariantProvider variant="glass">
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
				<DropdownMenuContent align="start">
					<DisabledReason
						disabled={hasMergeRequest}
						reason={t("git.merge.error.merge-request-instant-merge")}
					>
						<DropdownMenuItem onSelect={instantMerge}>
							<Icon code="merge" />
							{t("git.merge.instant-merge")}
						</DropdownMenuItem>
					</DisabledReason>
					<DisabledReason disabled={hasMergeRequest} reason={t("git.merge.error.merge-request-exists")}>
						<DropdownMenuItem onSelect={setCreateMergeRequestModal}>
							<Icon code="git-pull-request-arrow" />
							{t("git.merge-requests.create")}
						</DropdownMenuItem>
					</DisabledReason>
					<DeleteItem
						confirm={{
							title: t("git.branch.delete.confirm.title"),
							description: (
								<span
									className="article"
									dangerouslySetInnerHTML={{
										// biome-ignore lint/style/useNamingConvention: intended
										__html: t("git.branch.delete.confirm.description").replace(
											"{{branch}}",
											branchName,
										),
									}}
								/>
							),
							icon: "alert-circle",
							status: "warning",
						}}
						isLoading={isLoading}
						onConfirm={deleteBranch}
					/>
				</DropdownMenuContent>
			</DropdownMenu>
		</ComponentVariantProvider>
	);
};

export default BranchMenu;
